import {
  useMutation,
  UseMutationOptions,
  UseQueryOptions,
  useQuery,
  UseQueryResult,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  keepPreviousData,
  UseInfiniteQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAppDispatch } from '@/hooks/use.redux';
import { defaultGetNextParam, defaultGetPreviousParam } from '../utils';
import { ApiEndpoint, DataQueryDto, MongoQueryTypesEnum, Paginated, generateAxios } from '@matap/shared';

type EndpointBody<T> = T extends ApiEndpoint<infer B, any, any, any> ? B : never;
type EndpointParams<T> = T extends ApiEndpoint<any, infer P, any, any> ? P : never;
type EndpointQuery<T> = T extends ApiEndpoint<any, any, infer Q, any> ? Q : never;
type EndpointResponse<T> = T extends ApiEndpoint<any, any, any, infer R> ? R : never;

type IsNever<T> = [T] extends [never] ? true : false;

type IsEmpty<T> = [T] extends [never] | [void] | [undefined] ? true : false;

// 1. Define the raw structure of all possible inputs
type RawPayload<T extends ApiEndpoint> = {
  body: EndpointBody<T>;
  params: EndpointParams<T>;
  query: EndpointQuery<T>;
};

type CleanPayload<T extends ApiEndpoint> = {
  [K in keyof RawPayload<T> as IsEmpty<RawPayload<T>[K]> extends true ? never : K]: RawPayload<T>[K];
};

type MutationPayload<T extends ApiEndpoint> = keyof CleanPayload<T> extends never ? void : CleanPayload<T>;

type QueryPayload<T extends ApiEndpoint> = (IsNever<EndpointQuery<T>> extends true ? { query?: never } : { query: EndpointQuery<T> }) &
  (IsNever<EndpointParams<T>> extends true ? { params?: never } : { params: EndpointParams<T> });

export function generateMutation<TEndpoint extends ApiEndpoint>(
  endpoint: TEndpoint,
  fn: (endpoint: TEndpoint, dispatch: ReturnType<typeof useAppDispatch>) => UseMutationOptions<EndpointResponse<TEndpoint>, AxiosError, MutationPayload<TEndpoint>>,
) {
  return function useGeneratedMutation(
    options?: UseMutationOptions<EndpointResponse<TEndpoint>, AxiosError, MutationPayload<TEndpoint>>,
  ): UseMutationResult<EndpointResponse<TEndpoint>, AxiosError, MutationPayload<TEndpoint>> {
    const dispatch = useAppDispatch();
    return useMutation<EndpointResponse<TEndpoint>, AxiosError, MutationPayload<TEndpoint>>({
      ...fn(endpoint, dispatch),
      ...options,
    });
  };
}

// ─── Query ────────────────────────────────────────────────

export function generateQuery<TEndpoint extends ApiEndpoint>(
  endpoint: TEndpoint,
  fn: (
    endpoint: TEndpoint,
    payload: QueryPayload<TEndpoint>,
    dispatch: ReturnType<typeof useAppDispatch>,
  ) => UseQueryOptions<EndpointResponse<TEndpoint>, AxiosError, EndpointResponse<TEndpoint>>,
) {
  return function useGeneratedQuery(
    payload: QueryPayload<TEndpoint>,
    options?: Omit<UseQueryOptions<EndpointResponse<TEndpoint>, AxiosError, EndpointResponse<TEndpoint>>, 'queryKey'>,
  ): UseQueryResult<EndpointResponse<TEndpoint>, AxiosError> {
    const dispatch = useAppDispatch();
    return useQuery<EndpointResponse<TEndpoint>, AxiosError, EndpointResponse<TEndpoint>>({
      ...fn(endpoint, payload, dispatch),
      ...options,
    });
  };
}

// ─── Infinite Query ───────────────────────────────────────

export function generateInfiniteQuery<TEndpoint extends ApiEndpoint>(
  endpoint: TEndpoint,
  fn: (
    endpoint: TEndpoint,
    payload: QueryPayload<TEndpoint>,
    dispatch: ReturnType<typeof useAppDispatch>,
  ) => Omit<
    UseInfiniteQueryOptions<EndpointResponse<TEndpoint>, AxiosError, EndpointResponse<TEndpoint>>,
    'getNextPageParam' | 'getPreviousPageParam' | 'initialPageParam'
  >,
) {
  return function useGeneratedInfiniteQuery(
    payload: QueryPayload<TEndpoint>,
    options?: Omit<
      UseInfiniteQueryOptions<EndpointResponse<TEndpoint>, AxiosError, EndpointResponse<TEndpoint>>,
      'queryKey' | 'getNextPageParam' | 'getPreviousPageParam' | 'initialPageParam'
    >,
  ): UseInfiniteQueryResult<EndpointResponse<TEndpoint>, AxiosError> {
    const dispatch = useAppDispatch();
    return useInfiniteQuery<EndpointResponse<TEndpoint>, AxiosError, EndpointResponse<TEndpoint>>({
      initialPageParam: 1,
      getNextPageParam: defaultGetNextParam,
      getPreviousPageParam: defaultGetPreviousParam,
      placeholderData: keepPreviousData,
      ...fn(endpoint, payload, dispatch),
      ...options,
    });
  };
}

// ─── Data Query ────────────────────────────────────────────

export function generateDataQueryHook<TEntity, TEndpoint extends ApiEndpoint<any, any, DataQueryDto<TEntity>, any>>(
  endpoint: TEndpoint,
  config?: {
    queryKey?: string;
    serializeQueryKey?: (query: EndpointQuery<TEndpoint>) => unknown[];
    gcTime?: number;
  },
) {
  const cfg = {
    queryKey: 'dataQuery',
    serializeQueryKey: (query: any) => [DataQueryDto.toQueryKey(query)],
    ...config,
  };

  return function useGeneratedDataQuery<TResult = Paginated<TEntity>>(
    payload: QueryPayload<TEndpoint>,
    options?: Omit<UseQueryOptions<TResult, AxiosError, TResult>, 'queryKey'>,
  ): UseQueryResult<TResult, AxiosError> {
    const queryKey = [cfg.queryKey, ...cfg.serializeQueryKey(payload.query as any)];

    if ((payload.query as DataQueryDto)?.type === MongoQueryTypesEnum.PAGINATE) {
      return useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam }) => generateAxios(endpoint, { ...payload, query: { ...payload.query, page: pageParam } }).then((r: any) => r.data),
        initialPageParam: 1,
        getNextPageParam: defaultGetNextParam,
        getPreviousPageParam: defaultGetPreviousParam,
        placeholderData: keepPreviousData,
        gcTime: cfg.gcTime,
        ...(options as any),
      }) as any;
    }

    return useQuery({
      queryKey,
      queryFn: () => generateAxios(endpoint, payload).then((r: any) => r.data),
      ...options,
    }) as any;
  };
}
